// components/sections/FinancialLiabilitySection.js
import React from 'react';
import InteractiveField from '../common/InteractiveField';
import MultilineField from '../common/MultilineField';
import { convertAmountToWords, getRublesForm } from '../../utils/currency';

/**
 * Компонент для раздела "6. Ответственность Сторон".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 */
const FinancialLiabilitySection = ({ data, onChange }) => {
  // Безопасная деструктуризация с фолбэками
  const {
    latePayment = { enabled: false, gracePeriodDays: 5, penaltyPercent: 0.1 },
    lateHandover = { enabled: false, gracePeriodDays: 5, penaltyPercent: 0.1 },
    propertyDamage = {
      enabled: false,
      options: {
        fine: { enabled: false, amount: 10000, days: 10 },
        replacement: { enabled: false, days: 10 },
        compensation: { enabled: false, days: 10 },
      },
    },
    otherConditions = { enabled: false, text: '' },
  } = data;

  const handleNestedChange = (section, field, value) => {
    const newData = { ...data, [section]: { ...data[section], [field]: value } };

    // Если включаем секцию "Порча имущества"
    if (section === 'propertyDamage' && field === 'enabled' && value === true) {
      const { options } = newData.propertyDamage;
      const hasEnabledOption = Object.values(options).some(opt => opt.enabled);
      // И если ни одна опция не выбрана, включаем "Возмещение стоимости" по умолчанию
      if (!hasEnabledOption) {
        newData.propertyDamage.options.compensation.enabled = true;
      }
    }
    onChange(newData);
  };

  const handleDamageOptionChange = (option, field, value) => {
    const newOptions = {
      ...propertyDamage.options,
      [option]: {
        ...propertyDamage.options[option],
        [field]: value,
      },
    };

    // Если меняем поле enabled и выключаем его
    if (field === 'enabled' && value === false) {
      const hasEnabledOption = Object.values(newOptions).some(opt => opt.enabled);
      
      // Если не осталось включенных опций, выключаем родительский тоггл
      if (!hasEnabledOption) {
        onChange({ 
          ...data, 
          propertyDamage: { 
            ...propertyDamage, 
            enabled: false,
            options: newOptions 
          } 
        });
        return;
      }
    }

    const newPropertyDamage = {
      ...propertyDamage,
      options: newOptions,
    };
    onChange({ ...data, propertyDamage: newPropertyDamage });
  };

  // Счетчик для динамической нумерации
  let clauseCounter = 1;

  // Формируем массив JSX-элементов для списка
  const activeDamageOptions = [
    propertyDamage.options.fine.enabled && (
      <p key="fine" className="document-preamble list-item">
        - уплаты штрафа в размере{' '}
        <InteractiveField value={propertyDamage.options.fine.amount} onChange={(v) => handleDamageOptionChange('fine', 'amount', v)} placeholder="10000" type="number" integerOnly={true} isRequired={true} />{' '}
        {propertyDamage.options.fine.amount && (
          <>
            <span className="amount-words-wrapper">
              (<span>
                {convertAmountToWords(propertyDamage.options.fine.amount)}
              </span>)
            </span>
          </>
        )}{' '}
        {getRublesForm(propertyDamage.options.fine.amount)} в{' '}
        <InteractiveField value={propertyDamage.options.fine.days} onChange={(v) => handleDamageOptionChange('fine', 'days', v)} placeholder="10" type="number" isRequired={true} />
        -дневный срок с момента предъявления такого требования;
      </p>
    ),
    propertyDamage.options.replacement.enabled && (
      <p key="replacement" className="document-preamble list-item">
        - замены испорченного имущества на аналогичное по качеству и характеристикам в{' '}
        <InteractiveField value={propertyDamage.options.replacement.days} onChange={(v) => handleDamageOptionChange('replacement', 'days', v)} placeholder="10" type="number" isRequired={true} />
        -дневный срок с момента предъявления такого требования;
      </p>
    ),
    propertyDamage.options.compensation.enabled && (
      <p key="compensation" className="document-preamble list-item">
        - возмещения стоимости испорченного имущества в{' '}
        <InteractiveField value={propertyDamage.options.compensation.days} onChange={(v) => handleDamageOptionChange('compensation', 'days', v)} placeholder="10" type="number" isRequired={true} />
        -дневный срок с момента предъявления такого требования.
      </p>
    ),
  ].filter(Boolean); // Убираем неактивные опции

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        6. Ответственность Сторон
      </h2>
      {/* --- Пункт 6.1 (Базовый) --- */}
      <p className="document-preamble">
        <b>6.{clauseCounter}.</b> За неисполнение или ненадлежащее исполнение обязательств по настоящему договору Стороны несут ответственность в соответствии с действующим законодательством Российской Федерации.
      </p>
      {/* --- Пункт 6.2 (Неустойка за просрочку платежа) --- */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button className={`option-pill-toggle ${latePayment.enabled ? 'selected' : ''}`} onClick={() => handleNestedChange('latePayment', 'enabled', !latePayment.enabled)}>
            {latePayment.enabled ? '✓ Ответственность за несвоевременную оплату' : '+ Ответственность за несвоевременную оплату'}
          </button>
        </div>
        {latePayment.enabled && (
          <p className="document-preamble">
            <b>6.{++clauseCounter}.</b> В случае просрочки внесения платы за Помещение и предоставленные услуги, Наймодатель вправе требовать уплаты неустойки в{' '}
            <InteractiveField value={latePayment.gracePeriodDays} onChange={(v) => handleNestedChange('latePayment', 'gracePeriodDays', v)} placeholder="5" type="number" isRequired={true} />
            -дневный срок в размере{' '}
            <InteractiveField value={latePayment.penaltyPercent} onChange={(v) => handleNestedChange('latePayment', 'penaltyPercent', v)} placeholder="0,1" type="number" isRequired={true} />
            % от неуплаченной суммы за каждый день просрочки.
          </p>
        )}
      </div>
      {/* --- Пункт 6.3 (Неустойка за просрочку передачи Помещения) --- */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button className={`option-pill-toggle ${lateHandover.enabled ? 'selected' : ''}`} onClick={() => handleNestedChange('lateHandover', 'enabled', !lateHandover.enabled)}>
            {lateHandover.enabled ? '✓ Ответственность за несвоевременную передачу' : '+ Ответственность за несвоевременную передачу'}
          </button>
        </div>
        {lateHandover.enabled && (
          <p className="document-preamble">
            <b>6.{++clauseCounter}.</b> В случае просрочки передачи Помещения Сторона, права которой были нарушены, вправе требовать уплаты неустойки в{' '}
            <InteractiveField value={lateHandover.gracePeriodDays} onChange={(v) => handleNestedChange('lateHandover', 'gracePeriodDays', v)} placeholder="5" type="number" isRequired={true} />
            -дневный срок в размере{' '}
            <InteractiveField value={lateHandover.penaltyPercent} onChange={(v) => handleNestedChange('lateHandover', 'penaltyPercent', v)} placeholder="0,1" type="number" isRequired={true} />
            % от ежемесячной суммы платы за Помещение за каждый день просрочки.
          </p>
        )}
      </div>
      {/* --- Пункт 6.4 (Порча имущества) --- */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button className={`option-pill-toggle ${propertyDamage.enabled ? 'selected' : ''}`} onClick={() => handleNestedChange('propertyDamage', 'enabled', !propertyDamage.enabled)}>
            {propertyDamage.enabled ? '✓ Ответственность за порчу имущества' : '+ Ответственность за порчу имущества'}
          </button>
        </div>
        {propertyDamage.enabled && (
          <div>
            <p className="document-preamble">
              <b>6.{++clauseCounter}.</b> В случае порчи имущества Наймодателя в результате виновных действий или бездействий Нанимателя, Наймодатель вправе требовать выполнения одного или нескольких из следующих условий:
            </p>
            
            <div className="indented-list-block">
                <div style={{ marginBottom: '12px' }}>
                    <button
                        className={`option-pill-toggle ${propertyDamage.options.fine.enabled ? 'selected' : ''}`}
                        onClick={() => handleDamageOptionChange('fine', 'enabled', !propertyDamage.options.fine.enabled)}
                        style={{ marginBottom: '8px' }}
                    >
                        {propertyDamage.options.fine.enabled ? '✓ Уплата штрафа' : '+ Уплата штрафа'}
                    </button>
                    {propertyDamage.options.fine.enabled && (
                        <p className="document-preamble list-item">
                            - уплаты штрафа в размере <InteractiveField value={propertyDamage.options.fine.amount} onChange={(v) => handleDamageOptionChange('fine', 'amount', v)} placeholder="10000" type="number" integerOnly={true} isRequired={true} />{' '}
                            {propertyDamage.options.fine.amount && (
                              <>
                                <span className="amount-words-wrapper">
                                  (<span>
                                    {convertAmountToWords(propertyDamage.options.fine.amount)}
                                  </span>)
                                </span>
                              </>
                            )}{' '}
                            {getRublesForm(propertyDamage.options.fine.amount)} в <InteractiveField value={propertyDamage.options.fine.days} onChange={(v) => handleDamageOptionChange('fine', 'days', v)} placeholder="10" type="number" isRequired={true} />-дневный срок с момента предъявления такого требования;
                        </p>
                    )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                    <button
                        className={`option-pill-toggle ${propertyDamage.options.replacement.enabled ? 'selected' : ''}`}
                        onClick={() => handleDamageOptionChange('replacement', 'enabled', !propertyDamage.options.replacement.enabled)}
                        style={{ marginBottom: '8px' }}
                    >
                        {propertyDamage.options.replacement.enabled ? '✓ Замена имущества' : '+ Замена имущества'}
                    </button>
                    {propertyDamage.options.replacement.enabled && (
                        <p className="document-preamble list-item">
                            - замены испорченного имущества на аналогичное по качеству и характеристикам в <InteractiveField value={propertyDamage.options.replacement.days} onChange={(v) => handleDamageOptionChange('replacement', 'days', v)} placeholder="10" type="number" isRequired={true} />-дневный срок с момента предъявления такого требования;
                        </p>
                    )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                    <button
                        className={`option-pill-toggle ${propertyDamage.options.compensation.enabled ? 'selected' : ''}`}
                        onClick={() => handleDamageOptionChange('compensation', 'enabled', !propertyDamage.options.compensation.enabled)}
                        style={{ marginBottom: '8px' }}
                    >
                        {propertyDamage.options.compensation.enabled ? '✓ Возмещение стоимости' : '+ Возмещение стоимости'}
                    </button>
                    {propertyDamage.options.compensation.enabled && (
                        <p className="document-preamble list-item">
                            - возмещения стоимости испорченного имущества в <InteractiveField value={propertyDamage.options.compensation.days} onChange={(v) => handleDamageOptionChange('compensation', 'days', v)} placeholder="10" type="number" isRequired={true} />-дневный срок с момента предъявления такого требования.
                        </p>
                    )}
                </div>
            </div>
          </div>
        )}
      </div>
      {/* --- Пункт 6.5 (Иные условия) --- */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button className={`option-pill-toggle ${otherConditions.enabled ? 'selected' : ''}`} onClick={() => handleNestedChange('otherConditions', 'enabled', !otherConditions.enabled)}>
            {otherConditions.enabled ? '✓ Иные условия ответственности' : '+ Иные условия ответственности'}
          </button>
        </div>
        {otherConditions.enabled && (
          <p className="document-preamble">
            <b>6.{++clauseCounter}.</b>{' '}
            <MultilineField value={otherConditions.text} onChange={(v) => handleNestedChange('otherConditions', 'text', v)} placeholder="При выявлении факта курения в квартире, Наниматель выплачивает штраф в размере 5000 рублей за каждый случай" isRequired={true} minRows={1} maxRows={5} />
          </p>
        )}
      </div>
    </div>
  );
};

export default FinancialLiabilitySection;